<template>
  <component
    :is="rootTag"
    class="review-card"
    :to="experienceUrl || undefined"
    target="_blank"
    @click="emit('click')"
  >
    <div class="review-card__header">
      <div class="review-card__reviewer">
        <img
          v-if="reviewerAvatar"
          :src="reviewerAvatar"
          :alt="reviewerName"
          class="review-card__avatar"
          loading="lazy"
        />
        <div
          v-else
          class="review-card__avatar review-card__avatar--fallback"
        >
          {{ avatarLetter }}
        </div>
        <div class="review-card__reviewer-content">
          <div class="review-card__reviewer-name">{{ reviewerName }}</div>
          <div class="review-card__meta">
            <span>{{ createdAt }}</span>
            <span v-if="travelerLevel">{{ travelerLevelLabel }}</span>
          </div>
        </div>
      </div>
      <div class="review-card__rating">
        <span class="review-card__rating-star">★</span>
        <span>{{ normalizedRating }}</span>
      </div>
    </div>

    <div class="review-card__body">
      <div class="review-card__experience">{{ experienceTitle }}</div>
      <div class="review-card__text">{{ reviewText }}</div>
    </div>
  </component>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { AppLink } from '@experience/core-app-link-lib';

  interface Props {
    rating: number;
    experienceTitle?: string;
    experienceUrl?: string;
    reviewerName: string;
    reviewerAvatar?: string;
    createdAt: string;
    travelerLevel?: number;
    reviewText: string;
  }

  const props = defineProps<Props>();

  const emit = defineEmits<{
    click: [];
  }>();

  const rootTag = computed(() => (props.experienceUrl ? AppLink : 'div'));
  const avatarLetter = computed(() => props.reviewerName.trim().charAt(0).toUpperCase());
  const normalizedRating = computed(() => props.rating.toFixed(1));
  const travelerLevelLabel = computed(() => `Уровень путешественника ${props.travelerLevel}`);
</script>

<style scoped lang="scss">
  @use '@experience/design-tokens/typography';

  .review-card {
    display: flex;
    flex-direction: column;
    gap: 16px;
    box-sizing: border-box;
    min-height: 100%;
    padding: 20px;
    color: var(--alias-color-text-default);
    text-decoration: none;
    background: var(--alias-color-background-primary);
    border: 1px solid var(--alias-color-border-subtle);
    border-radius: 24px;
  }

  .review-card__header {
    display: flex;
    gap: 12px;
    justify-content: space-between;
    align-items: flex-start;
  }

  .review-card__reviewer {
    display: flex;
    gap: 12px;
    align-items: center;
    min-width: 0;
  }

  .review-card__avatar {
    flex-shrink: 0;
    width: 48px;
    height: 48px;
    object-fit: cover;
    border-radius: 50%;
  }

  .review-card__avatar--fallback {
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--alias-color-text-default);
    background: var(--alias-color-background-secondary);

    @include typography.base-500-400-regular;
  }

  .review-card__reviewer-content {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }

  .review-card__reviewer-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    @include typography.base-500-400-regular;
  }

  .review-card__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 8px;
    color: var(--alias-color-text-secondary);

    @include typography.base-300-300-regular;
  }

  .review-card__rating {
    display: flex;
    gap: 4px;
    align-items: center;
    flex-shrink: 0;

    @include typography.base-500-400-regular;
  }

  .review-card__rating-star {
    color: var(--alias-color-icon-warning);
  }

  .review-card__body {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
  }

  .review-card__experience {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    @include typography.base-500-400-regular;
  }

  .review-card__text {
    display: -webkit-box;
    overflow: hidden;
    color: var(--alias-color-text-secondary);
    -webkit-line-clamp: 6;
    -webkit-box-orient: vertical;

    @include typography.base-400-400-regular;
  }
</style>
